import { Checkbox, CheckboxGroup, Flex, Text, RadioButtonGroup, RadioButton, Avatar } from '@pega/cosmos-react-core';
import React, { ChangeEvent, useEffect, useRef, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import useLocalTranslation from '../../../../hooks/useLocalTranslation';
import { appState } from '../../../../interfaces/common';
import { InteractiveText } from '../../../Utilities/custom.styled';
import { setChatInfo, updateBannerInfo, updateLoaderDetails } from '../../../../redux/actions';
import { enableAvatarsPayload, enableMessageFontColorPayload } from '../../../../interfaces/chat';
import { useNavigate } from 'react-router-dom';
import { deleteAvatarImage, getImageMetaData, updateUIToggle, validateUploadAvatarImg } from '../ChatApis';
import ColorPicker from '../../../Utilities/ColorPicker';

interface UploadAvatarProps {
  imgUrl?: string;
  avatarType: 'csrAvatar' | 'botAvatar' | 'userAvatar';
  fileRef?: React.MutableRefObject<any>;
  onFileChange: any;
  uploadText: string;
  sessionId: string;
  connectionId: string;
  isAvatarHidden?: boolean;
  showSDKConfigs?: boolean;
  enableAvatars?: boolean;
  enableMessageFontColor?: boolean;
}

const avatarConfigs = {
  botAvatar: 'Bot_Update',
  csrAvatar: 'Csr_Update',
  userAvatar: 'User_Update',
};

const UploadAvatar: React.FC<UploadAvatarProps> = ({
  imgUrl,
  avatarType,
  fileRef,
  onFileChange,
  uploadText,
  sessionId,
  connectionId,
  isAvatarHidden,
  showSDKConfigs = false,
  enableAvatars = false,
  enableMessageFontColor = false,
}) => {
  const translate = useLocalTranslation();
  const [defaultAvatar, setDefaultAvatar] = useState(!imgUrl && !isAvatarHidden);
  const [customAvatar, setCustomAvatar] = useState(Boolean(imgUrl));
  const [hidden, setHidden] = useState(isAvatarHidden);
  const dispatch = useDispatch();
  const navigate = useNavigate();

  useEffect(() => {
    setHidden(isAvatarHidden);
  }, [isAvatarHidden]);

  useEffect(() => {
    setDefaultAvatar(!imgUrl && !isAvatarHidden);
    setCustomAvatar(Boolean(imgUrl) && !isAvatarHidden);
  }, [imgUrl, isAvatarHidden]);

  const updateAvatarType = async (e: ChangeEvent<HTMLInputElement>) => {
    const type = e.target.value;
    const isDefault = type === 'default';
    const isCustom = type === 'custom';
    const isHidden = type === 'hidden';
    setDefaultAvatar(isDefault);
    setCustomAvatar(isCustom);
    setHidden(isHidden);
    const key =
      avatarType === 'botAvatar'
        ? 'isBotAvatarHidden'
        : avatarType === 'csrAvatar'
        ? 'isCsrAvatarHidden'
        : 'isUserAvatarHidden';
    if (imgUrl) {
      let json;
      dispatch(updateLoaderDetails(true));
      if (isDefault) {
        json = await deleteAvatarImage(sessionId, connectionId, { avatar: avatarType });
      }
      if (isHidden || (imgUrl && isCustom) || isDefault) {
        json = await updateUIToggle('update_avatar_hidden_status', sessionId, connectionId, {
          [key]: isHidden,
          type: avatarConfigs[avatarType],
        });
      }
      dispatch(updateLoaderDetails(false));
      updateBannerAndChat(json);
    } else if ((!imgUrl && isAvatarHidden && isDefault) || (!imgUrl && isHidden)) {
      dispatch(updateLoaderDetails(true));
      const json = await updateUIToggle('update_avatar_hidden_status', sessionId, connectionId, {
        [key]: isHidden,
        type: avatarConfigs[avatarType],
      });
      dispatch(updateLoaderDetails(false));
      updateBannerAndChat(json);
    }
  };

  const updateBannerAndChat = (json: any) => {
    if (json?.status === 200) {
      dispatch(setChatInfo(json?.data));
      dispatch(updateBannerInfo(translate(`${avatarType}_update_success`), 'success'));
    } else if (json?.status === 403) {
      navigate('/');
      dispatch(updateBannerInfo(translate('invalid_session'), 'urgent'));
    } else {
      dispatch(updateBannerInfo(translate(`${avatarType}_update_failed`), 'urgent'));
    }
  };
  return (
    <Flex container={{ direction: 'column', itemGap: 0.5 }} style={{ width: '40%' }}>
      {enableAvatars && (
        <RadioButtonGroup
          label={translate(`${avatarType}_label`)}
          name={`${avatarType}-selection`}
          onChange={updateAvatarType}
        >
          <RadioButton label={translate('default_avatar')} value={'default'} checked={defaultAvatar} />
          <RadioButton label={translate('custom_avatar')} value={'custom'} checked={customAvatar} />
          <RadioButton label={translate('hidden_avatar')} value={'hidden'} checked={hidden} />
        </RadioButtonGroup>
      )}
      {!enableAvatars && enableMessageFontColor && <Text variant="secondary">{translate(`${avatarType}`)}</Text>}
      {enableAvatars && (
        <Flex container={{ direction: 'row', itemGap: 0.5 }}>
          {(imgUrl || defaultAvatar) && !hidden ? (
            defaultAvatar && avatarType === 'botAvatar' ? (
              <Avatar variant="primary" name="" icon="robot-solid" shape="circle" data-testid="bot_avatar" />
            ) : defaultAvatar && avatarType === 'userAvatar' ? (
              <Avatar variant="primary" name="" icon="user-solid" shape="circle" data-testid="user_avatar" />
            ) : (
              !hidden && <Avatar name="John Doe" imageSrc={imgUrl} />
            )
          ) : (
            !hidden && (
              <Flex
                style={{
                  height: 32,
                  width: 32,
                  borderRadius: '50%',
                  backgroundColor: '#d8d8d8',
                }}
              ></Flex>
            )
          )}
          {defaultAvatar && !hidden ? (
            <Text style={{ paddingTop: 5 }}>
              {avatarType === 'botAvatar'
                ? translate('default_bot')
                : avatarType === 'userAvatar'
                ? translate('default_user')
                : translate('default_csr')}
            </Text>
          ) : hidden ? (
            <Text style={{ paddingTop: 7, paddingBottom: 6 }}>{translate('hidden')}</Text>
          ) : (
            !hidden && (
              <>
                <Text style={{ paddingTop: 5 }}>
                  <InteractiveText
                    className="pointer"
                    onClick={() => {
                      fileRef?.current.click();
                    }}
                  >
                    {uploadText}
                  </InteractiveText>
                </Text>
                <input
                  type="file"
                  accept=".png,.jpeg,.jpg"
                  ref={fileRef}
                  onChange={onFileChange}
                  style={{ display: 'none' }}
                />
              </>
            )
          )}
        </Flex>
      )}
      {enableMessageFontColor && !showSDKConfigs && (
        <ColorPicker labelText={translate('message_font_color')} avatarType={avatarType} colorType="FontColor" />
      )}
      {enableMessageFontColor && !showSDKConfigs && (
        <ColorPicker labelText={translate('message_bg_color')} avatarType={avatarType} colorType="BgColor" />
      )}
    </Flex>
  );
};

export interface EnableAvatarProps {
  showSDKConfigs?: boolean;
}

const EnableAvatar: React.FC<EnableAvatarProps> = ({ showSDKConfigs }) => {
  const translate = useLocalTranslation();
  const fileRefBot = useRef<any>(null);
  const fileRefCsr = useRef<any>(null);
  const fileRefUser = useRef<any>(null);
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const {
    chatInfo: {
      connectionId,
      enableAvatars,
      csrAvatar,
      botAvatar,
      userAvatar,
      isBotAvatarHidden = false,
      isCsrAvatarHidden = false,
      isUserAvatarHidden = false,
      enableMessageFontColor,
    },
    sessionId,
  } = useSelector((state: appState) => {
    return {
      chatInfo: state.chatInfo,
      sessionId: state.sessionId,
    };
  });

  const updateDisplayAvatarOption = async (e: any) => {
    await updateToggleConfig('display_avatars', {
      enableAvatars: e.target.checked,
    });
  };

  const updateMessageFontColor = async (e: any) => {
    await updateToggleConfig('update_message_font_color', {
      enableMessageFontColor: e.target.checked,
      defaultFontColor: '#000000',
      defaultBgColor: '#e9ebef',
      defaultUserFontColor: '#ffffff',
      enableDefaultColor: !showSDKConfigs,
    });
  };

  const updateToggleConfig = async (
    togglePath: string,
    payload: enableAvatarsPayload | enableMessageFontColorPayload,
  ) => {
    dispatch(updateLoaderDetails(true));
    let json = await updateUIToggle(togglePath, sessionId, connectionId, payload);
    dispatch(updateLoaderDetails(false));
    if (json?.status === 200) {
      dispatch(setChatInfo(json?.data));
      dispatch(updateBannerInfo(translate(`${togglePath}_update_success`), 'success'));
    } else if (json?.status === 403) {
      navigate('/');
      dispatch(updateBannerInfo(translate('invalid_session'), 'urgent'));
    } else {
      dispatch(updateBannerInfo(translate(`${togglePath}_update_failed`), 'urgent'));
    }
  };

  const validateFile = async (requestId: string, avatarImg: string) => {
    let response;
    try {
      response = await validateUploadAvatarImg(sessionId, connectionId, requestId, avatarImg);
    } catch (error) {
      dispatch(updateBannerInfo(translate('avatar_upload_failed'), 'urgent'));
    }
    dispatch(updateLoaderDetails(false));
    if (response?.status === 200) {
      let value = false;
      let key = '';
      if (avatarImg === 'bot') {
        key = 'isBotAvatarHidden';
        value = isBotAvatarHidden;
      } else if (avatarImg === 'csr') {
        key = 'isCsrAvatarHidden';
        value = isCsrAvatarHidden;
      } else if (avatarImg === 'user') {
        key = 'isUserAvatarHidden';
        value = isUserAvatarHidden;
      }
      dispatch(setChatInfo(response.data));
      if (value) {
        dispatch(updateLoaderDetails(true));
        const json = await updateUIToggle('update_avatar_hidden_status', sessionId, connectionId, {
          [key]: false,
          type: avatarImg === 'bot' ? 'Bot_Update' : avatarImg === 'csr' ? 'Csr_Update' : 'User_Update',
        });
        dispatch(updateLoaderDetails(false));
        if (json?.status === 200) {
          dispatch(setChatInfo(json?.data));
        }
      }
      dispatch(updateBannerInfo(translate('avatar_upload_success'), 'success'));
    } else if (response?.status === 403) {
      navigate('/');
      dispatch(updateBannerInfo(translate('invalid_session'), 'urgent'));
    } else {
      dispatch(updateBannerInfo(translate('avatar_upload_failed'), 'urgent'));
    }
  };

  const uploadFile = async (urlAndFields: any, file: File, avatarImg: string) => {
    const { url, fields } = urlAndFields;
    const requestId = fields['x-amz-meta-request-id'];
    const formData = new FormData();
    Object.keys(fields).forEach((key) => {
      formData.append(key, fields[key]);
    });
    formData.append('file', file);
    await fetch(url, {
      method: 'POST',
      body: formData,
    })
      .then(() => {
        if (requestId) {
          validateFile(requestId, avatarImg);
        }
      })
      .catch(() => {
        dispatch(updateLoaderDetails(false));
        dispatch(updateBannerInfo(translate('avatar_upload_failed'), 'urgent'));
      });
  };

  const getUploadUrl = async (file: File, avatarImg: string) => {
    dispatch(updateLoaderDetails(true));
    let response = await getImageMetaData(sessionId, connectionId);
    if (response?.status === 200) {
      uploadFile(response.data, file, avatarImg);
    } else if (response?.status === 403) {
      dispatch(updateLoaderDetails(false));
      navigate('/');
      dispatch(updateBannerInfo(translate('invalid_session'), 'urgent'));
    } else {
      dispatch(updateLoaderDetails(false));
      dispatch(updateBannerInfo(translate('avatar_upload_failed'), 'urgent'));
    }
  };

  const onBotFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    validateFileType(event, 'bot');
  };

  const onCsrFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    validateFileType(event, 'csr');
  };

  const onUserFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    validateFileType(event, 'user');
  };

  const validateFileType = async (event: React.ChangeEvent<HTMLInputElement>, avatarImg: string) => {
    if (event.currentTarget.files && event.currentTarget.files.length > 0) {
      const file = event.currentTarget.files[0];
      const fileType = file?.name?.split('.')?.pop() as string;
      if (['png', 'jpeg', 'jpg'].includes(fileType.toLocaleLowerCase())) {
        getUploadUrl(file, avatarImg);
      }
    }
  };

  return (
    <Flex
      container={{
        direction: 'column',
        itemGap: 2,
      }}
    >
      <CheckboxGroup name="enableAvatar">
        <Checkbox
          label={translate(showSDKConfigs ? 'avatar_option_sdk' : 'avatar_option')}
          checked={enableAvatars}
          onChange={updateDisplayAvatarOption}
        />
      </CheckboxGroup>
      {!showSDKConfigs && (
        <Checkbox
          label={translate('update_message_font_color')}
          name="messageFontColorToggle"
          checked={enableMessageFontColor}
          onChange={updateMessageFontColor}
        />
      )}
      {(enableAvatars || enableMessageFontColor) && (
        <Flex container={{ direction: 'column', itemGap: 3 }}>
          <Flex container={{ direction: 'row', itemGap: 8 }}>
            <UploadAvatar
              imgUrl={botAvatar}
              uploadText={translate('upload_image_bot')}
              onFileChange={onBotFileChange}
              fileRef={fileRefBot}
              avatarType="botAvatar"
              sessionId={sessionId}
              connectionId={connectionId}
              isAvatarHidden={isBotAvatarHidden}
              showSDKConfigs={showSDKConfigs}
              enableAvatars={enableAvatars}
              enableMessageFontColor={enableMessageFontColor}
            />
            <UploadAvatar
              imgUrl={csrAvatar}
              uploadText={translate('upload_image_csr')}
              onFileChange={onCsrFileChange}
              fileRef={fileRefCsr}
              avatarType="csrAvatar"
              sessionId={sessionId}
              connectionId={connectionId}
              isAvatarHidden={isCsrAvatarHidden}
              showSDKConfigs={showSDKConfigs}
              enableAvatars={enableAvatars}
              enableMessageFontColor={enableMessageFontColor}
            />
          </Flex>
          <Flex container={{ direction: 'row', itemGap: 8 }}>
            <UploadAvatar
              imgUrl={userAvatar}
              uploadText={translate('upload_image_user')}
              onFileChange={onUserFileChange}
              fileRef={fileRefUser}
              avatarType="userAvatar"
              sessionId={sessionId}
              connectionId={connectionId}
              isAvatarHidden={isUserAvatarHidden}
              showSDKConfigs={showSDKConfigs}
              enableAvatars={enableAvatars}
              enableMessageFontColor={enableMessageFontColor}
            />
          </Flex>
          {enableAvatars && (
            <Flex>
              <Text>
                <b>{translate('note')}:</b> {translate('widget_avatar_note')}
              </Text>
            </Flex>
          )}
        </Flex>
      )}
    </Flex>
  );
};

export default EnableAvatar;
